import siteMetadata from "./config/siteMetadata.json";
import {
  createProfileStructuredData,
  serializeProfileStructuredData,
} from "./config/profileStructuredData";
import {
  createNewsStructuredData,
  serializeNewsStructuredData,
} from "./config/newsStructuredData";
import {
  createScheduleStructuredData,
  serializeScheduleStructuredData,
} from "./config/scheduleStructuredData";
import { newsItems } from "./data/news";
import { scheduleItems } from "./data/scheduleEvents";

const PROFILE_STRUCTURED_DATA_ATTRIBUTE =
  "data-profile-image-structured-data";
const NEWS_STRUCTURED_DATA_ATTRIBUTE = "data-news-structured-data";
const SCHEDULE_STRUCTURED_DATA_ATTRIBUTE = "data-schedule-structured-data";
const VIDEO_STRUCTURED_DATA_ATTRIBUTE = "data-video-structured-data";
const VIDEO_BREADCRUMB_STRUCTURED_DATA_ATTRIBUTE =
  "data-video-breadcrumb-structured-data";
const VIDEO_META_PROPERTIES = Object.freeze([
  "og:video",
  "og:video:width",
  "og:video:height",
  "og:video:type",
]);
const scheduleJpegSourcesByKey = Object.freeze({
  schedule20260919_1: require("./assets/images/schedule20260919_1.jpg"),
  schedule20260919_2: require("./assets/images/schedule20260919_2.jpg"),
  schedule20260922_1: require("./assets/images/schedule20260922_1.jpg"),
  schedule20260922_2: require("./assets/images/schedule20260922_2.jpg"),
  schedule20261212_1: require("./assets/images/schedule20261212_1.jpg"),
  schedule20261212_2: require("./assets/images/schedule20261212_2.jpg"),
});

const createScheduleImageUrlsByKey = () => {
  const imageKeys = [
    ...new Set(
      scheduleItems.flatMap((item) =>
        Array.isArray(item.imageKeys) ? item.imageKeys : []
      )
    ),
  ];

  return imageKeys.reduce((imageUrlsByKey, imageKey) => {
    const importedSource = scheduleJpegSourcesByKey[imageKey];
    const assetPath = importedSource &&
      (importedSource.default || importedSource);

    if (typeof assetPath !== "string" || !assetPath.trim()) {
      throw new Error(`Missing browser schedule image: ${imageKey}`);
    }

    const emittedUrl = new URL(assetPath, `${siteMetadata.site.origin}/`);
    imageUrlsByKey[imageKey] = new URL(
      emittedUrl.pathname,
      `${siteMetadata.site.origin}/`
    ).href;

    return imageUrlsByKey;
  }, {});
};

const scheduleImageUrlsByKey = createScheduleImageUrlsByKey();

const upsertMeta = (attribute, key, content) => {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute("content", String(content));
};

const removeElements = (selector) => {
  document.head.querySelectorAll(selector).forEach((element) => {
    element.parentNode.removeChild(element);
  });
};

const upsertCanonical = (href) => {
  let element = document.head.querySelector("link[rel='canonical']");

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
};

const updateStructuredDataElement = ({ attribute, enabled, serialize }) => {
  const elements = Array.from(
    document.head.querySelectorAll(`script[${attribute}]`)
  );
  const [existingElement, ...duplicateElements] = elements;

  duplicateElements.forEach((element) => {
    element.parentNode.removeChild(element);
  });

  if (!enabled) {
    if (existingElement) {
      existingElement.parentNode.removeChild(existingElement);
    }
    return;
  }

  const element = existingElement || document.createElement("script");
  element.type = "application/ld+json";
  element.setAttribute(attribute, "true");
  element.textContent = serialize();

  if (!existingElement) {
    document.head.appendChild(element);
  }
};

const updateStructuredData = (page, canonicalUrl) => {
  const site = siteMetadata.site;

  updateStructuredDataElement({
    attribute: PROFILE_STRUCTURED_DATA_ATTRIBUTE,
    enabled: Boolean(page.profileImageStructuredData && canonicalUrl),
    serialize: () =>
      serializeProfileStructuredData(
        createProfileStructuredData({ canonicalUrl, page, site })
      ),
  });
  updateStructuredDataElement({
    attribute: NEWS_STRUCTURED_DATA_ATTRIBUTE,
    enabled: Boolean(page.newsStructuredData && canonicalUrl),
    serialize: () =>
      serializeNewsStructuredData(
        createNewsStructuredData({
          canonicalUrl,
          newsItems,
          page,
          site,
        })
      ),
  });
  updateStructuredDataElement({
    attribute: SCHEDULE_STRUCTURED_DATA_ATTRIBUTE,
    enabled: Boolean(page.scheduleStructuredData && canonicalUrl),
    serialize: () =>
      serializeScheduleStructuredData(
        createScheduleStructuredData({
          canonicalUrl,
          imageUrlsByKey: scheduleImageUrlsByKey,
          page,
          scheduleItems,
          site,
        })
      ),
  });

  if (!page.videoId) {
    removeElements(`script[${VIDEO_STRUCTURED_DATA_ATTRIBUTE}]`);
    removeElements(`script[${VIDEO_BREADCRUMB_STRUCTURED_DATA_ATTRIBUTE}]`);
    VIDEO_META_PROPERTIES.forEach((property) => {
      removeElements(`meta[property="${property}"]`);
    });
  }
};

const resolveCanonicalUrl = (path) => {
  const url = new URL(path, `${siteMetadata.site.origin}/`);

  if (url.protocol !== "https:" || url.origin !== siteMetadata.site.origin) {
    return `${siteMetadata.site.origin}/`;
  }

  return url.href;
};

const resolvePageMetadata = (route) => {
  const routeMetadata = route.matched.reduce(
    (metadata, record) => ({ ...metadata, ...record.meta }),
    {}
  );

  if (
    !routeMetadata.title ||
    !routeMetadata.description ||
    !routeMetadata.robots
  ) {
    throw new Error(`Missing document metadata for route: ${route.fullPath}`);
  }

  return routeMetadata;
};

export const updateDocumentMetadata = (route) => {
  const page = resolvePageMetadata(route);
  const site = siteMetadata.site;
  let canonicalUrl = null;

  document.title = page.title;

  if (page.canonicalPath) {
    canonicalUrl = resolveCanonicalUrl(page.canonicalPath);
    upsertCanonical(canonicalUrl);
    upsertMeta("property", "og:url", canonicalUrl);
  } else {
    removeElements("link[rel='canonical']");
    removeElements('meta[property="og:url"]');
  }

  updateStructuredData(page, canonicalUrl);

  upsertMeta("name", "description", page.description);
  upsertMeta("name", "robots", page.robots);
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", page.title);
  upsertMeta("name", "twitter:description", page.description);
  upsertMeta("name", "twitter:image", site.image);
  upsertMeta("name", "twitter:image:alt", site.imageAlt);

  upsertMeta("property", "og:type", site.type);
  upsertMeta("property", "og:title", page.title);
  upsertMeta("property", "og:description", page.description);
  upsertMeta("property", "og:image", site.image);
  upsertMeta("property", "og:image:type", site.imageType);
  upsertMeta("property", "og:image:width", site.imageWidth);
  upsertMeta("property", "og:image:height", site.imageHeight);
  upsertMeta("property", "og:image:alt", site.imageAlt);
  upsertMeta("property", "og:site_name", site.name);
  upsertMeta("property", "og:locale", site.locale);
};
